import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import styled from 'styled-components';
import {Detail, Icon} from '@forecast-it/design-system';
import Util from '../../../forecast-app/shared/util/util';
import {FEATURE_FLAG_LINK_TEXT} from './featureFlagSurface';
import {FEATURE_FLAG_UI_STORAGE_KEY, FeatureFlagUi, featureFlagUrl, pathForFeatureFlagUi} from './featureFlagUiPreference';

const SwitchLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 12px;
	text-decoration: none;

	/* The typography components paint their own colour onto the element they render, so a colour
	   inherited from this link would lose to it and has to be set on the children instead. The arrow
	   takes its colour from its own prop and is unaffected. */
	> * {
		color: ${FEATURE_FLAG_LINK_TEXT};
	}

	&:hover {
		text-decoration: underline;
	}
`;

const LABELS: Record<FeatureFlagUi, string> = {
	old: 'Go back to the old UI',
	new: 'Go to the new UI',
};

export interface FeatureFlagUiSwitchLinkProps {
	/** The screen to go to, which is also the preference this link stores. */
	target: FeatureFlagUi;
}

/**
 * Moves between the two feature flag screens, and remembers the move.
 *
 * The preference is written here, on the click, rather than anywhere a screen renders. A screen that
 * stored "I am the one you want" on mount would pin whichever page an operator happened to open first
 * — including the one they only reached because it is today's default — and there would then be no
 * way to change that default for anyone who had ever visited. Written on the click, the stored value
 * only ever means "this is the one I asked for".
 *
 * Both screens are rendered by this one link, in opposite directions, so the two can never drift into
 * saying different things or storing different values.
 */
export function FeatureFlagUiSwitchLink({target}: FeatureFlagUiSwitchLinkProps): JSX.Element {
	const {pathname} = useLocation();

	return (
		<SwitchLink
			data-cy={`feature-flag-ui-switch-${target}`}
			to={featureFlagUrl(pathname, pathForFeatureFlagUi(target))}
			onClick={() => Util.localStorageSetItem(FEATURE_FLAG_UI_STORAGE_KEY, target)}
		>
			<Icon size="s" icon="arrowLeft" newColor="primary" />
			<Detail>{LABELS[target]}</Detail>
		</SwitchLink>
	);
}
